



































































import { Call } from '@/shared/call';
import { CallReason } from '@/shared/call-reason';
import { CallType } from '@/shared/call-type';
import { CallUser } from '@/shared/call-user';
import { ClientOriginalSourceType } from '@/shared/types/client-original-source-type';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const partnerPracticesModule = namespace('partnerPractices');
const typesModule = namespace('types');

@Component
export default class CallsTable extends Vue {
    @partnerPracticesModule.Getter('partnerPracticeName') private partnerPracticeName!: (partnerPracticeId: string | null) => string;

    @typesModule.Getter('clientOriginalSourceTypes') private clientOriginalSourceTypes!: Array<ClientOriginalSourceType>;

    @Prop({ required: true }) public calls!: Array<Call>;
    @Prop({ required: true }) public page!: number;
    @Prop({ required: true }) public pageSize!: number;
    @Prop({ required: true }) public total!: number;

    private currentPage: number = this.page;

    private rows: Array<Call & { selected: boolean }> = new Array<Call & { selected: boolean }>();
    private selectedCalls: Array<Call & { selected: boolean }> = new Array<Call & { selected: boolean }>();
    private selectAll: boolean = false;

    private get paginationDescription(): string {
        const start = (this.calls.length < 1) ? 0 : ((this.currentPage - 1) * this.pageSize) + 1;
        const end = start + this.pageSize > this.total ? this.total : start + this.pageSize - 1;
        return `Showing ${start.toLocaleString()} - ${end.toLocaleString()} of ${this.total.toLocaleString()}`;
    }

    private get canAssignCalls(): boolean {
        return this.selectedCalls.length > 0;
    }

    private get canUnassignCalls(): boolean {
        return this.selectedCalls.length > 0 && this.selectedCalls.some((call) => call.users.length > 0);
    }

    private get assignCallsText(): string {
        if (this.selectedCalls.length === 1 && this.selectedCalls.some((call) => call.users.length > 0)) {
            return 'Re-assign Call';
        } else if (this.selectedCalls.length > 1 && this.selectedCalls.some((call) => call.users.length > 0)) {
            return 'Re-assign Calls';
        } else if (this.selectedCalls.length > 1) {
            return 'Assign Calls';
        } else {
            return 'Assign Call';
        }
    }

    public get unassignCallsText(): string {
        return this.selectedCalls.length > 1 ? 'Un-assign Calls' : 'Un-assign Call';
    }

    @Watch('page')
    private onPageChanged(page: number) {
        this.currentPage = page;
    }

    @Watch('currentPage')
    private onCurrentPageChanged(): void {
        this.$emit('page-changed', this.currentPage);
    }

    @Watch('selectAll')
    private onSelectAllChanged() {
        this.rows.forEach((row) => row.selected = this.selectAll);
    }

    @Watch('calls')
    private onCallsChanged(calls: Array<Call>): void {
        this.rows = [];
        calls.forEach((call) => this.rows.push({ ...call, selected: false }));
    }

    @Watch('rows', { deep: true })
    private onRowsChanged(): void {
        this.selectedCalls = this.rows.filter((row) => row.selected);
    }

    @Watch('selectedCalls')
    private onSelectedCallsChanged(): void {
        if (this.selectedCalls.length === 0) { this.selectAll = false; }
    }

    private clientName(call: Call): string {
        return `${call.firstName!.trim()} ${call.lastName!.trim()}`.trim();
    }

    private callTypeText(type: CallType): string | null {
        switch (type) {
            case CallType.Call:
                return 'Call';
            case CallType.Callback:
                return 'Callback';
            default:
                return '';
        }
    }

    private callReasonText(reason: CallReason): string | null {
        switch (reason) {
            case CallReason.General:
                return 'General';
            case CallReason.CancelledAppointment:
                return 'Appointment cancelled';
            case CallReason.GeneralReview:
                return 'General Review';
            case CallReason.NoShow:
                return 'No Show';
            default:
                return '';
        }
    }

    private originalSourceDescription(originalSourceId: string | null): string {
        const originalSource = this.clientOriginalSourceTypes.find((type) => type.id === originalSourceId!);
        return originalSource == null ? '' : originalSource.value;
    }

    private formatAssignedUsers(users: Array<CallUser>): string {
        const names = users.map((user) => `${user.firstName!.trim()} ${user.lastName!.trim()}`.trim());
        return names
            .sort((a, b) => {
                if (a > b) { return 1; }
                if (a < b) { return -1; }
                return 0;
            })
            .join(', ');
    }

    private async onAssignCalls(): Promise<void> {
        this.$emit('assign-calls', [...this.selectedCalls]);
    }

    private onUnassignCalls(): void {
        this.$emit('unassign-calls', [...this.selectedCalls]);
    }
}

